import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **ARIA tabs**: one tab has `tabindex="0"`, others `-1` (**roving tabindex**). Arrow keys call `show()` then `.focus()`
 * so keyboard order matches visual selection. Active panel drops `hidden`; others set it (WAI-ARIA tab pattern).
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'tabs-keyboard',
  category: 'navigation-composed',
  title: 'Tabs + keyboard',
  description: 'Arrow Left/Right moves selection; activates panel visibility.',
  tags: [T.tabs, T.keyboard, T.a11y],
  html: `<h2>Tabs</h2>

<div class="tabs" role="tablist" aria-label="Demo">
  <button role="tab" id="tab0" aria-selected="true" aria-controls="pan0">One</button>
  <button role="tab" id="tab1" aria-selected="false" aria-controls="pan1" tabindex="-1">Two</button>
  <button role="tab" id="tab2" aria-selected="false" aria-controls="pan2" tabindex="-1">Three</button>
</div>

<div id="pan0" role="tabpanel" aria-labelledby="tab0" class="pan">Panel one</div>
<div id="pan1" role="tabpanel" aria-labelledby="tab1" class="pan" hidden>Panel two</div>
<div id="pan2" role="tabpanel" aria-labelledby="tab2" class="pan" hidden>Panel three</div>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 1rem;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--nogrok-preview-outline);
  max-width: 28rem;
}

[role='tab'] {
  font: inherit;
  padding: 0.5rem 1rem;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: var(--nogrok-preview-surface-rail);
  color: var(--nogrok-preview-on-surface-variant);
  cursor: pointer;
}

/* Selected tab visually connects to panel below (shared bottom border color) */
[role='tab'][aria-selected='true'] {
  background: var(--nogrok-preview-surface-container);
  color: var(--nogrok-preview-primary);
  border-color: var(--nogrok-preview-outline);
  border-bottom-color: var(--nogrok-preview-surface-container);
  margin-bottom: -1px;
}

.pan {
  padding: 1rem;
  border: 1px solid var(--nogrok-preview-outline);
  border-top: 0;
  border-radius: 0 0 10px 10px;
  background: var(--nogrok-preview-surface-container);
  max-width: 28rem;
  min-height: 4rem;
}`,
  js: `const tabs = [...document.querySelectorAll('[role=tab]')]
let ix = 0

function show(i) {
  ix = ((i % tabs.length) + tabs.length) % tabs.length
  tabs.forEach((t, j) => {
    const on = j === ix
    t.setAttribute('aria-selected', String(on))
    t.tabIndex = on ? 0 : -1
    const id = t.getAttribute('aria-controls')
    const p = id ? document.getElementById(id) : null
    if (on) p?.removeAttribute('hidden')
    else p?.setAttribute('hidden', '')
  })
}

tabs.forEach((t, i) => {
  t.addEventListener('click', () => {
    show(i)
    t.focus()
  })
  t.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      show(ix + 1)
      tabs[ix].focus()
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      show(ix - 1)
      tabs[ix].focus()
    }
  })
})`,
}

export default preset
